import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { env } from '@repo/environment';
import { axios } from '@repo/http';
import { Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Textarea } from '@repo/ui-primitives';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@repo/ui-primitives/dropdown-menu';
import { useCurrentUser } from '@repo/user-context';
import { useMutation } from '@tanstack/react-query';
import { MessageCircle, Send, X } from 'lucide-react';
import { useState } from 'react';
const submitFeedback = async (data)=>{
    await axios.post(env.PUBLIC_FEEDBACK_API_URL, data);
};
const FEEDBACK_TOPICS = [
    {
        value: 'bug',
        label: '🐛 Bug Report'
    },
    {
        value: 'feature',
        label: '✨ Feature Request'
    },
    {
        value: 'ui',
        label: '🎨 UI/UX Issue'
    },
    {
        value: 'performance',
        label: '⚡ Performance Issue'
    },
    {
        value: 'docs',
        label: '📚 Documentation'
    },
    {
        value: 'general',
        label: '💬 General Feedback'
    }
];
export function FeedbackButton() {
    const [isOpen, setIsOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [topic, setTopic] = useState('');
    const [isSuccess, setIsSuccess] = useState(false);
    const { user } = useCurrentUser();
    const feedbackMutation = useMutation({
        mutationFn: submitFeedback,
        onSuccess: ()=>{
            setIsSuccess(true);
            setMessage('');
            setTopic('');
            setTimeout(()=>{
                setIsSuccess(false);
                setIsOpen(false);
            }, 2000);
        }
    });
    const handleSubmit = (e)=>{
        e.preventDefault();
        if (!message.trim() || !topic) {
            return;
        }
        feedbackMutation.mutate({
            message: message.trim(),
            topic,
            user: user === null || user === void 0 ? void 0 : user.name,
            email: user === null || user === void 0 ? void 0 : user.email,
            url: window.location.href,
            timestamp: new Date().toISOString()
        });
    };
    const handleClose = ()=>{
        if (!feedbackMutation.isPending) {
            setMessage('');
            setTopic('');
            feedbackMutation.reset();
            setIsSuccess(false);
            setIsOpen(false);
        }
    };
    return /*#__PURE__*/ _jsxs(DropdownMenu, {
        open: isOpen,
        onOpenChange: setIsOpen,
        children: [
            /*#__PURE__*/ _jsx(DropdownMenuTrigger, {
                asChild: true,
                children: /*#__PURE__*/ _jsxs(Button, {
                    variant: "outline",
                    size: "sm",
                    className: "gap-2",
                    children: [
                        /*#__PURE__*/ _jsx(MessageCircle, {
                            className: "h-4 w-4"
                        }),
                        "Feedback"
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(DropdownMenuContent, {
                side: "bottom",
                align: "end",
                sideOffset: 8,
                className: "w-80 p-4",
                onInteractOutside: (e)=>{
                    if (feedbackMutation.isPending) {
                        e.preventDefault();
                    }
                },
                children: isSuccess ? /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-col items-center justify-center py-6 text-center",
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: "w-8 h-8 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center mb-3",
                            children: /*#__PURE__*/ _jsx("svg", {
                                className: "w-5 h-5 text-green-600 dark:text-green-300",
                                fill: "none",
                                stroke: "currentColor",
                                viewBox: "0 0 24 24",
                                children: /*#__PURE__*/ _jsx("path", {
                                    strokeLinecap: "round",
                                    strokeLinejoin: "round",
                                    strokeWidth: 2,
                                    d: "M5 13l4 4L19 7"
                                })
                            })
                        }),
                        /*#__PURE__*/ _jsx("h3", {
                            className: "font-medium mb-1 text-foreground",
                            children: "Feedback sent!"
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "text-sm text-gray-600 dark:text-gray-400",
                            children: "Thank you for helping us improve."
                        })
                    ]
                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center justify-between mb-3",
                            children: [
                                /*#__PURE__*/ _jsx("h3", {
                                    className: "font-medium text-foreground",
                                    children: "Send Feedback"
                                }),
                                /*#__PURE__*/ _jsx(Button, {
                                    variant: "ghost",
                                    size: "sm",
                                    onClick: handleClose,
                                    disabled: feedbackMutation.isPending,
                                    className: "h-6 w-6 p-0",
                                    "aria-label": "Close feedback",
                                    children: /*#__PURE__*/ _jsx(X, {
                                        className: "h-4 w-4 text-foreground"
                                    })
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("form", {
                            onSubmit: handleSubmit,
                            className: "space-y-3",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-2",
                                    children: [
                                        /*#__PURE__*/ _jsx("label", {
                                            htmlFor: "topic",
                                            className: "text-sm font-medium text-foreground",
                                            children: "Topic"
                                        }),
                                        /*#__PURE__*/ _jsxs(Select, {
                                            value: topic,
                                            onValueChange: (value)=>{
                                                setTopic(value);
                                                feedbackMutation.reset();
                                            },
                                            disabled: feedbackMutation.isPending,
                                            children: [
                                                /*#__PURE__*/ _jsx(SelectTrigger, {
                                                    id: "topic",
                                                    className: "text-sm text-foreground",
                                                    children: /*#__PURE__*/ _jsx(SelectValue, {
                                                        placeholder: "Select a topic...",
                                                        className: "text-foreground"
                                                    })
                                                }),
                                                /*#__PURE__*/ _jsx(SelectContent, {
                                                    children: FEEDBACK_TOPICS.map((topicOption)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                            value: topicOption.value,
                                                            children: topicOption.label
                                                        }, topicOption.value))
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-2",
                                    children: [
                                        /*#__PURE__*/ _jsx("label", {
                                            htmlFor: "feedback",
                                            className: "text-sm font-medium text-foreground",
                                            children: "Feedback"
                                        }),
                                        /*#__PURE__*/ _jsx(Textarea, {
                                            id: "feedback",
                                            placeholder: "Tell us what's on your mind...",
                                            value: message,
                                            onChange: (e)=>{
                                                setMessage(e.target.value);
                                                feedbackMutation.reset();
                                            },
                                            rows: 4,
                                            className: "resize-none text-sm text-foreground",
                                            disabled: feedbackMutation.isPending
                                        })
                                    ]
                                }),
                                feedbackMutation.error && /*#__PURE__*/ _jsx("div", {
                                    className: "text-xs text-destructive bg-destructive/10 p-2 rounded border border-destructive/50",
                                    children: feedbackMutation.error.message || 'Failed to send feedback. Please try again.'
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex justify-end gap-2",
                                    children: [
                                        /*#__PURE__*/ _jsx(Button, {
                                            type: "button",
                                            variant: "ghost",
                                            size: "sm",
                                            onClick: handleClose,
                                            disabled: feedbackMutation.isPending,
                                            className: "text-foreground hover:text-foreground",
                                            children: "Cancel"
                                        }),
                                        /*#__PURE__*/ _jsx(Button, {
                                            type: "submit",
                                            size: "sm",
                                            disabled: feedbackMutation.isPending || !message.trim() || !topic,
                                            className: "min-w-[80px]",
                                            children: feedbackMutation.isPending ? /*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center gap-1",
                                                children: [
                                                    /*#__PURE__*/ _jsx("div", {
                                                        className: "w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"
                                                    }),
                                                    "Sending..."
                                                ]
                                            }) : /*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center gap-1",
                                                children: [
                                                    /*#__PURE__*/ _jsx(Send, {
                                                        className: "w-3 h-3"
                                                    }),
                                                    "Send"
                                                ]
                                            })
                                        })
                                    ]
                                }),
                                user && /*#__PURE__*/ _jsxs("div", {
                                    className: "text-xs text-gray-600 dark:text-gray-400 border-t pt-3",
                                    children: [
                                        "From: ",
                                        /*#__PURE__*/ _jsx("strong", {
                                            className: "text-foreground",
                                            children: user.name
                                        }),
                                        " (",
                                        user.email,
                                        ")"
                                    ]
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
